import React, { useState, useEffect, useCallback } from 'react'
import { useOutletContext } from 'react-router-dom'
import { controlCenterApi, type AccountDetail, type DomainItem } from '../../services/controlCenterApi'

interface AccountContext {
  account: AccountDetail
  refreshAccount: () => void
}

const DNS_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  dns_pending: { label: 'Pending', color: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  dns_active: { label: 'Active', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  dns_error: { label: 'Error', color: 'text-red-400 bg-red-500/10 border-red-500/30' },
}

const SSL_STATUS_LABELS: Record<string, { label: string; color: string }> = {
  ssl_pending: { label: 'Pending', color: 'text-amber-400 bg-amber-500/10 border-amber-500/30' },
  ssl_active: { label: 'Active', color: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30' },
  ssl_error: { label: 'Error', color: 'text-red-400 bg-red-500/10 border-red-500/30' },
}

const TEMPLATE_OPTIONS = [
  { value: 'default', label: 'Default' },
  { value: 'modern', label: 'Modern' },
  { value: 'classic', label: 'Classic' },
  { value: 'minimal', label: 'Minimal' },
]

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/

export function AccountWebsiteTab(): React.ReactElement {
  const { account } = useOutletContext<AccountContext>()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [isDirty, setIsDirty] = useState(false)

  const [form, setForm] = useState({
    google_analytics_id: '',
    google_tag_manager_id: '',
    primary_color: '#f6931e',
    secondary_color: '#094673',
    template_name: 'default',
    custom_head_html: '',
    custom_footer_html: '',
    mobile_site_enabled: true,
  })

  const [colorErrors, setColorErrors] = useState<{ primary?: string; secondary?: string }>({})

  // Domain state
  const [domains, setDomains] = useState<DomainItem[]>([])
  const [newDomain, setNewDomain] = useState('')
  const [domainError, setDomainError] = useState('')
  const [addingDomain, setAddingDomain] = useState(false)
  const [removingDomainId, setRemovingDomainId] = useState<string | null>(null)
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null)

  const loadSettings = useCallback(async () => {
    setIsLoading(true)
    setError('')
    try {
      const [settingsData, domainsData] = await Promise.all([
        controlCenterApi.getWebsiteSettings(account.id),
        controlCenterApi.getDomains(account.id),
      ])
      setForm({
        google_analytics_id: settingsData.google_analytics_id || '',
        google_tag_manager_id: settingsData.google_tag_manager_id || '',
        primary_color: settingsData.primary_color || '#f6931e',
        secondary_color: settingsData.secondary_color || '#094673',
        template_name: settingsData.template_name || 'default',
        custom_head_html: settingsData.custom_head_html || '',
        custom_footer_html: settingsData.custom_footer_html || '',
        mobile_site_enabled: settingsData.mobile_site_enabled,
      })
      setDomains(domainsData)
      setIsDirty(false)
    } catch {
      setError('Failed to load website settings')
    }
    setIsLoading(false)
  }, [account.id])

  useEffect(() => {
    loadSettings()
  }, [loadSettings])

  const handleAddDomain = async () => {
    if (!newDomain.trim()) return
    setAddingDomain(true)
    setDomainError('')
    try {
      const created = await controlCenterApi.addDomain(account.id, newDomain.trim())
      setDomains(prev => [...prev, created])
      setNewDomain('')
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : 'Failed to add domain')
    }
    setAddingDomain(false)
  }

  const handleRemoveDomain = async (domainId: string) => {
    setRemovingDomainId(domainId)
    setDomainError('')
    try {
      await controlCenterApi.removeDomain(account.id, domainId)
      setDomains(prev => prev.filter(d => d.id !== domainId))
      setConfirmRemoveId(null)
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : 'Failed to remove domain')
    }
    setRemovingDomainId(null)
  }

  const handleSetPrimary = async (domainId: string) => {
    setDomainError('')
    try {
      await controlCenterApi.setDomainPrimary(account.id, domainId)
      setDomains(prev => prev.map(d => ({ ...d, is_primary: d.id === domainId })))
    } catch (err) {
      setDomainError(err instanceof Error ? err.message : 'Failed to set primary domain')
    }
  }

  const updateField = (field: string, value: string | boolean) => {
    setForm(prev => ({ ...prev, [field]: value }))
    setIsDirty(true)
    setSuccess('')

    if (field === 'primary_color' || field === 'secondary_color') {
      const colorKey = field === 'primary_color' ? 'primary' : 'secondary'
      if (typeof value === 'string' && value !== '' && !HEX_PATTERN.test(value)) {
        setColorErrors(prev => ({ ...prev, [colorKey]: 'Must be a valid hex color (e.g. #FF5733)' }))
      } else {
        setColorErrors(prev => ({ ...prev, [colorKey]: undefined }))
      }
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (colorErrors.primary || colorErrors.secondary) return

    setIsSaving(true)
    setError('')
    setSuccess('')
    try {
      await controlCenterApi.updateWebsiteSettings(account.id, form)
      setSuccess('Settings saved successfully')
      setIsDirty(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save settings')
    }
    setIsSaving(false)
  }

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-slate-400 text-sm mt-3">Loading website settings...</span>
      </div>
    )
  }

  if (error && !isDirty) {
    return (
      <div className="p-6 bg-red-500/10 border border-red-500/30 rounded-lg">
        <p className="text-red-300 text-sm">{error}</p>
        <button type="button" onClick={loadSettings} className="text-red-400 hover:text-red-300 text-sm mt-2 underline">
          Retry
        </button>
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Website Settings</h2>
          <p className="text-slate-400 text-sm mt-0.5">Configure website appearance and tracking for this account</p>
        </div>
        <button
          type="submit"
          disabled={isSaving || !isDirty || !!colorErrors.primary || !!colorErrors.secondary}
          className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {/* Status messages */}
      {success && (
        <div className="mb-5 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg flex items-center gap-2">
          <svg className="w-4 h-4 text-emerald-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
          <p className="text-emerald-300 text-sm">{success}</p>
        </div>
      )}
      {error && isDirty && (
        <div className="mb-5 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-red-300 text-sm">{error}</p>
        </div>
      )}

      <div className="space-y-6">
        {/* Domains */}
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Domains</h3>
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5">
            {domainError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-300 text-sm">{domainError}</p>
              </div>
            )}

            {/* Domain list */}
            {domains.length > 0 && (
              <div className="space-y-2 mb-4">
                {domains.map(d => (
                  <div key={d.id} className="flex items-center justify-between bg-slate-900 rounded-lg px-4 py-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm text-white font-medium truncate">{d.domain}</span>
                      {d.is_primary && (
                        <span className="text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary shrink-0">
                          Primary
                        </span>
                      )}
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${DNS_STATUS_LABELS[d.dns_status]?.color || ''}`}>
                        DNS: {DNS_STATUS_LABELS[d.dns_status]?.label || d.dns_status}
                      </span>
                      <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded border shrink-0 ${SSL_STATUS_LABELS[d.ssl_status]?.color || ''}`}>
                        SSL: {SSL_STATUS_LABELS[d.ssl_status]?.label || d.ssl_status}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-3">
                      {!d.is_primary && (
                        <button
                          type="button"
                          onClick={() => handleSetPrimary(d.id)}
                          className="text-xs text-slate-400 hover:text-primary transition-colors"
                        >
                          Set Primary
                        </button>
                      )}
                      {confirmRemoveId === d.id ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => handleRemoveDomain(d.id)}
                            disabled={removingDomainId === d.id}
                            className="text-xs text-red-400 hover:text-red-300 font-medium disabled:opacity-50"
                          >
                            {removingDomainId === d.id ? 'Removing...' : 'Confirm'}
                          </button>
                          <button
                            type="button"
                            onClick={() => setConfirmRemoveId(null)}
                            className="text-xs text-slate-500 hover:text-slate-300"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          type="button"
                          onClick={() => setConfirmRemoveId(d.id)}
                          className="text-xs text-slate-500 hover:text-red-400 transition-colors"
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {domains.length === 0 && (
              <p className="text-slate-500 text-sm mb-4">No domains configured for this account.</p>
            )}

            {/* Add domain */}
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={newDomain}
                onChange={e => { setNewDomain(e.target.value); setDomainError('') }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddDomain() } }}
                placeholder="dealer-website.com"
                className="flex-1 bg-slate-900 border border-slate-600 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors"
              />
              <button
                type="button"
                onClick={handleAddDomain}
                disabled={addingDomain || !newDomain.trim()}
                className="px-4 py-2.5 bg-slate-700 text-white text-sm font-medium rounded-lg hover:bg-slate-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {addingDomain ? 'Adding...' : 'Add Domain'}
              </button>
            </div>
          </div>
        </section>

        {/* Tracking & Analytics */}
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Tracking & Analytics</h3>
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 space-y-4">
            <div>
              <label htmlFor="ws-ga" className="block text-sm font-medium text-slate-300 mb-1.5">Google Analytics ID</label>
              <input
                id="ws-ga"
                type="text"
                value={form.google_analytics_id}
                onChange={e => updateField('google_analytics_id', e.target.value)}
                placeholder="G-XXXXXXXXXX or UA-XXXXX-X"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors"
              />
            </div>
            <div>
              <label htmlFor="ws-gtm" className="block text-sm font-medium text-slate-300 mb-1.5">Google Tag Manager ID</label>
              <input
                id="ws-gtm"
                type="text"
                value={form.google_tag_manager_id}
                onChange={e => updateField('google_tag_manager_id', e.target.value)}
                placeholder="GTM-XXXXXXX"
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors"
              />
            </div>
          </div>
        </section>

        {/* Appearance */}
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Appearance</h3>
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 space-y-5">
            {/* Color pickers in a row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="ws-primary" className="block text-sm font-medium text-slate-300 mb-1.5">Primary Color</label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg border border-slate-600 shrink-0 relative overflow-hidden"
                    style={{ backgroundColor: HEX_PATTERN.test(form.primary_color) ? form.primary_color : '#666' }}
                  >
                    <input
                      type="color"
                      value={HEX_PATTERN.test(form.primary_color) ? form.primary_color : '#f6931e'}
                      onChange={e => updateField('primary_color', e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      aria-label="Pick primary color"
                    />
                  </div>
                  <input
                    id="ws-primary"
                    type="text"
                    value={form.primary_color}
                    onChange={e => updateField('primary_color', e.target.value)}
                    placeholder="#f6931e"
                    className={`flex-1 bg-slate-900 border rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 transition-colors ${
                      colorErrors.primary
                        ? 'border-red-500/50 focus:ring-red-500/30 focus:border-red-500/50'
                        : 'border-slate-600 focus:ring-primary/50 focus:border-primary/50'
                    }`}
                  />
                </div>
                {colorErrors.primary && <p className="text-red-400 text-xs mt-1.5">{colorErrors.primary}</p>}
              </div>
              <div>
                <label htmlFor="ws-secondary" className="block text-sm font-medium text-slate-300 mb-1.5">Secondary Color</label>
                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-lg border border-slate-600 shrink-0 relative overflow-hidden"
                    style={{ backgroundColor: HEX_PATTERN.test(form.secondary_color) ? form.secondary_color : '#666' }}
                  >
                    <input
                      type="color"
                      value={HEX_PATTERN.test(form.secondary_color) ? form.secondary_color : '#094673'}
                      onChange={e => updateField('secondary_color', e.target.value)}
                      className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      aria-label="Pick secondary color"
                    />
                  </div>
                  <input
                    id="ws-secondary"
                    type="text"
                    value={form.secondary_color}
                    onChange={e => updateField('secondary_color', e.target.value)}
                    placeholder="#094673"
                    className={`flex-1 bg-slate-900 border rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 transition-colors ${
                      colorErrors.secondary
                        ? 'border-red-500/50 focus:ring-red-500/30 focus:border-red-500/50'
                        : 'border-slate-600 focus:ring-primary/50 focus:border-primary/50'
                    }`}
                  />
                </div>
                {colorErrors.secondary && <p className="text-red-400 text-xs mt-1.5">{colorErrors.secondary}</p>}
              </div>
            </div>

            {/* Color preview strip */}
            {(HEX_PATTERN.test(form.primary_color) || HEX_PATTERN.test(form.secondary_color)) && (
              <div className="flex items-center gap-3">
                <span className="text-xs text-slate-500">Preview</span>
                <div className="flex-1 h-3 rounded-full overflow-hidden flex">
                  <div className="w-1/2" style={{ backgroundColor: HEX_PATTERN.test(form.primary_color) ? form.primary_color : '#ccc' }} />
                  <div className="w-1/2" style={{ backgroundColor: HEX_PATTERN.test(form.secondary_color) ? form.secondary_color : '#ccc' }} />
                </div>
              </div>
            )}

            {/* Template */}
            <div>
              <label htmlFor="ws-template" className="block text-sm font-medium text-slate-300 mb-1.5">Template</label>
              <select
                id="ws-template"
                value={form.template_name}
                onChange={e => updateField('template_name', e.target.value)}
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3.5 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors appearance-none"
                style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='m6 8 4 4 4-4'/%3E%3C/svg%3E")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.5em 1.5em', paddingRight: '2.5rem' }}
              >
                {TEMPLATE_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Mobile site toggle */}
            <div className="flex items-center justify-between pt-1">
              <div>
                <span className="text-sm font-medium text-white">Mobile Site</span>
                <p className="text-xs text-slate-400 mt-0.5">Enable mobile-optimized version of the dealer website</p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={form.mobile_site_enabled}
                aria-label="Toggle mobile site"
                onClick={() => updateField('mobile_site_enabled', !form.mobile_site_enabled)}
                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-slate-900 ${
                  form.mobile_site_enabled ? 'bg-primary' : 'bg-slate-600'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                    form.mobile_site_enabled ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>
          </div>
        </section>

        {/* Custom HTML */}
        <section>
          <h3 className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3">Custom HTML</h3>
          <div className="bg-slate-800 rounded-xl border border-slate-700 p-5 space-y-4">
            <div>
              <label htmlFor="ws-head" className="block text-sm font-medium text-slate-300 mb-1.5">
                Custom Head HTML
              </label>
              <p className="text-xs text-slate-500 mb-2">Injected into the &lt;head&gt; of the dealer website. Use for meta tags, tracking pixels, etc.</p>
              <textarea
                id="ws-head"
                value={form.custom_head_html}
                onChange={e => updateField('custom_head_html', e.target.value)}
                rows={4}
                placeholder='<meta name="custom" content="value">'
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors font-mono resize-y"
              />
            </div>
            <div>
              <label htmlFor="ws-footer" className="block text-sm font-medium text-slate-300 mb-1.5">
                Custom Footer HTML
              </label>
              <p className="text-xs text-slate-500 mb-2">Injected before &lt;/body&gt;. Use for chat widgets, custom scripts, etc.</p>
              <textarea
                id="ws-footer"
                value={form.custom_footer_html}
                onChange={e => updateField('custom_footer_html', e.target.value)}
                rows={4}
                placeholder='<script src="https://example.com/widget.js"></script>'
                className="w-full bg-slate-900 border border-slate-600 rounded-lg px-3.5 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-colors font-mono resize-y"
              />
            </div>
          </div>
        </section>
      </div>

      {/* Sticky save bar when dirty */}
      {isDirty && (
        <div className="mt-6 p-4 bg-slate-800/80 backdrop-blur border border-slate-700 rounded-xl flex items-center justify-between">
          <p className="text-sm text-slate-300">You have unsaved changes</p>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={loadSettings}
              disabled={isSaving}
              className="px-4 py-2 text-sm text-slate-400 hover:text-white transition-colors disabled:opacity-40"
            >
              Discard
            </button>
            <button
              type="submit"
              disabled={isSaving || !!colorErrors.primary || !!colorErrors.secondary}
              className="px-5 py-2 bg-primary text-white text-sm font-medium rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {isSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </form>
  )
}
